import React, { ReactNode, useContext, useEffect } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  AppBar,
  Box,
  Grid2 as Grid,
  ScopedCssBaseline,
  Slide,
  Toolbar,
  Typography,
} from '@mui/material';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import LogoIcon from '../../../common/components/LogoIcon';
import { Carriers } from '../../../common/constants/carriers';
import { ComponentIds } from '../../../common/constants/componentIds';
import { Products } from '../../../common/constants/products';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { AuthContext } from '../../../features/login/AuthWrapper';
import { UserPanel } from '../../dashboard/UserPanel';
import { resetApplication } from '../../tasks/slice/taskThunks';
import Back from '../sidebars/icons/Back';
import './header.scss';
import { NoraBar } from './NoraBar';
import { ReferralNotificationBadge } from './ReferralNotificationBadge';
const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const AppHeader = ({ isBackButton }: { isBackButton?: boolean }) => {
  const auth = useContext(AuthContext);
  const dispatch: AppDispatch = useDispatch();
  const onBackClick = () => {
    dispatch(resetApplication());
  };

  const leftChild = isBackButton ? (
    <Typography variant="h6" color="textSecondary">
      <Back onBackClick={onBackClick} />
    </Typography>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <LogoIcon width={24} height={24} />
      <div className="navbar__text__container">
        <p className="navbar__text navbar__text__main">Ethos</p>
        <p className="navbar__text navbar__text__sub">Workbench</p>
      </div>
    </div>
  );

  const name: string = useSelector(state => state.application.customerName);
  const policy = useSelector(state => state.application.carrierPolicyNumber);
  const coverage = useSelector(state => state.application.coverage);
  const product = useSelector(state => state.application.product);
  const subCategory = useSelector(state => state.application.subCategory);
  const carrier = useSelector(state => state.application.carrier);

  const stageV2: string = useSelector(
    state => state.application.applicationStageV2,
  );
  const applicationLifecycleV2: string = useSelector(
    state => state.application.applicationLifecycleV2,
  );
  const detailedLifecycle: string = useSelector(
    state => state.application.applicationDetailedLifecycle,
  );

  const appId = useSelector(state => state.application.applicationId);
  const lastDecision: string = useSelector(
    state => state.application.lastDecisionType,
  );

  let bannerMsg = '';
  const { id: applicationId } = useParams() as { id: any };
  const applicationDate = useSelector(state => state.application.creationDate);

  if (
    isBackButton &&
    appId === +applicationId &&
    carrier === Carriers.LGA &&
    product === Products.TERM &&
    applicationDate
  ) {
    const applicationDts = Date.parse(applicationDate);
    if (
      applicationDts &&
      Date.parse('2021-10-11T03:21:00.000Z') <= applicationDts &&
      applicationDts <= Date.parse('2021-10-13T23:36:00.000Z')
    ) {
      bannerMsg = 'Use Streamlined Guidelines from Before 9/25';
    }
  }

  useEffect(() => {
    return () => {
      bannerMsg = '';
    };
  }, []);

  return (
    <React.Fragment>
      <ScopedCssBaseline>
        <AppBar
          color="inherit"
          id={ComponentIds.NORA_APP_HEADER_ID}
          sx={{ boxShadow: 0, borderBottom: '1px solid #E0E0E0' }}
        >
          <Toolbar variant="dense">
            {isBackButton && bannerMsg ? (
              <Box
                sx={{
                  position: 'absolute',
                  zIndex: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                <Typography variant="h3" color="error">
                  {bannerMsg}
                </Typography>
              </Box>
            ) : null}

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={5}
            >
              <Box display="flex" alignItems="center">
                {leftChild}
              </Box>

              <Box flexGrow={1} display="flex" justifyContent="center">
                <ShowOnScroll threshold={180}>
                  <Grid container justifyContent="center" alignItems="center">
                    {isBackButton && (
                      <NoraBar
                        name={name}
                        policy={policy}
                        coverage={coverage}
                        product={product}
                        subCategory={subCategory}
                        carrier={carrier}
                        stage={stageV2}
                        lastDecision={lastDecision}
                        bannerMsg={bannerMsg}
                        applicationLifecycleV2={applicationLifecycleV2}
                        applicationDetailedLifecycle={detailedLifecycle}
                      />
                    )}
                  </Grid>
                </ShowOnScroll>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                gap={2}
              >
                <ReferralNotificationBadge />
                <UserPanel name={auth.userName} />
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      </ScopedCssBaseline>
    </React.Fragment>
  );
};

function getScrollY(scroller: any) {
  return scroller.pageYOffset !== undefined
    ? scroller.pageYOffset
    : scroller.scrollTop !== undefined
      ? scroller.scrollTop
      : (document.documentElement || document.body.parentNode || document.body)
          .scrollTop;
}

const useShowOnScroll = ({
  threshold,
  scroller,
}: {
  threshold: number;
  scroller?: any;
}) => {
  const [show, setShow] = React.useState(false);

  const handleScroll = React.useCallback(() => {
    const scrollY = getScrollY(scroller || window);
    setShow(scrollY > threshold);
  }, [scroller, threshold]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return show;
};

const ShowOnScroll = ({
  children,
  threshold = 100,
  scroller,
  ...otherProps
}: {
  children: ReactNode;
  threshold?: number;
  scroller?: any;
}) => {
  const show = useShowOnScroll({ threshold, scroller });

  return (
    <Slide direction="down" in={show} timeout={300} appear {...otherProps}>
      <Box width="100%">{children}</Box>
    </Slide>
  );
};

export default AppHeader;
